import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, LogOut, Package, Search, Settings, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCartContext } from '@/contexts/CartContext';
import { useBrandTheme } from '@/contexts/ThemeContext';

export const Header: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuthContext();
  const { cart } = useCartContext();
  const { theme } = useBrandTheme();

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <header className="bg-white dark:bg-gray-900 shadow-soft border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 backdrop-blur-sm bg-white/95 dark:bg-gray-900/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div 
                className="h-10 w-10 rounded-xl flex items-center justify-center shadow-soft group-hover:shadow-medium transition-shadow duration-200"
                style={{
                  backgroundImage: `linear-gradient(to bottom right, ${theme?.colors.primary}, ${theme?.colors.primaryDark})`
                }}
              >
                <Package className="h-6 w-6 text-white" />
              </div>
            </div>
            <span 
              className="text-xl font-bold text-gray-900 dark:text-white transition-colors duration-200"
              style={{
                '--hover-color': theme?.colors.primary
              } as React.CSSProperties}
              onMouseEnter={(e) => e.currentTarget.style.color = theme?.colors.primary || ''}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
              {theme?.branding.siteName}
            </span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="input w-full pl-10 pr-4"
              />
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            
            {!isAdminRoute && (
              <Link
                to="/cart"
                className="relative p-2 nav-link rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.totalItems > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-scale-in"
                    style={{ backgroundColor: theme?.colors.primary }}
                  >
                    {cart.totalItems}
                  </span>
                )}
              </Link>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                {/* Admin Dashboard Link */}
                <Link to="/admin">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="nav-link flex items-center space-x-2"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Button>
                </Link>
                
                <div className="flex items-center space-x-2 text-sm nav-link">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user?.username}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="nav-link"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            ) : (
              <Link to="/admin/login">
                <Button variant="outline" size="sm" className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Admin Login</span>
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="md:hidden pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              className="input w-full pl-10 pr-4"
            />
          </div>
        </div>
      </div>
    </header>
  );
};
